import { Link } from 'react-router-dom';
import { Banner } from '@givebutter/primitives/banner';
import * as BannerPrimitive from '@givebutter/primitives/banner';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import { useOveragePosthogProperties } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useOveragePosthogProperties';
import {
    useOveragesShouldWarnBannerChecks,
    usePlanOverageUsageData,
} from 'assets/js/pages/account-settings/account-about/givebutter-plus/usePlanOverageUsageData';
import { usePermissions } from 'components/permissions';
import { AccountPermission } from 'components/permissions/PermissionEnums';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { posthogEvents } from 'constants/posthog-events';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { useRecommendedPlanComparison } from 'customHooks/useRecommendedPlanComparison';

const ContactsOverageBannersRecommendedPlan = () => {
    const { recommendedPlanDifference } = useRecommendedPlanComparison();
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { isGracePeriod } = usePlanOverageUsageData();
    const overagePosthogProps = useOveragePosthogProperties();
    const { shouldPromptToManagePlans } = useOveragesShouldWarnBannerChecks();
    const { pushEvent } = usePosthog();

    const {
        setHasDismissedOveragesWarningGracePeriodBanner,
        setHasDismissedOveragesWarningBanner,
    } = useAccountLocalStorage(
        ({
            setHasDismissedOveragesWarningGracePeriodBanner,
            setHasDismissedOveragesWarningBanner,
        }) => {
            return {
                setHasDismissedOveragesWarningGracePeriodBanner,
                setHasDismissedOveragesWarningBanner,
            };
        },
    );

    if (!validGivebutterPlusPlan || !shouldPromptToManagePlans) {
        return null;
    }

    const { tier } = validGivebutterPlusPlan;

    return (
        <Banner
            type="warning"
            onDismiss={() => {
                if (isGracePeriod) {
                    setHasDismissedOveragesWarningGracePeriodBanner(true);
                } else {
                    setHasDismissedOveragesWarningBanner(true);
                }

                pushEvent(posthogEvents.DASHBOARD_OVERAGES_CONTACTS_BANNER_DISMISSED, {
                    ...overagePosthogProps,
                });
            }}
        >
            <div className="w-full text-sm">
                <p className="font-medium">
                    You could save <CurrencySymbol />
                    {maskDecimal(recommendedPlanDifference)}{' '}
                    {tier.interval === 'month' ? '/mo' : '/year'} in overages by upgrading
                </p>
                <p className="text-gray-600">
                    You’re spending more in overages than the cost of the recommended plan.{' '}
                    {isGracePeriod
                        ? 'Consider upgrading to a higher-tier plan to avoid overage charges.'
                        : 'Upgrade now and save on overage charges.'}
                </p>
            </div>
            <div className="flex items-center gap-2">
                <OverageBannerManagePlanButton />
                <BannerPrimitive.DismissButton />
            </div>
        </Banner>
    );
};

interface IContactsOverageBannersLimitWarningProps extends BannerPrimitive.BannerProps {}
const ContactsOverageBannersLimitWarning = ({
    children,
    onDismiss,
    ...rest
}: IContactsOverageBannersLimitWarningProps) => {
    const { pushEvent } = usePosthog();
    const overagePosthogProps = useOveragePosthogProperties();

    return (
        <Banner
            type="warning"
            onDismiss={() => {
                pushEvent(posthogEvents.DASHBOARD_OVERAGES_CONTACTS_BANNER_DISMISSED, {
                    ...overagePosthogProps,
                });
                onDismiss?.();
            }}
            {...rest}
        >
            <div className="flex w-full flex-wrap items-center text-sm sm:gap-2">
                <p className="font-medium">{children}</p>
                <p className="text-gray-600">
                    Consider upgrading to a higher-tier plan to avoid overage charges.
                </p>
            </div>
            <div className="flex items-center gap-2">
                <OverageBannerManagePlanButton />
                <BannerPrimitive.DismissButton />
            </div>
        </Banner>
    );
};

const ContactsOverageBannersWarn80 = () => {
    const { shouldWarn80Used } = useOveragesShouldWarnBannerChecks();

    const { setHasDismissed80WarningBanner } = useAccountLocalStorage(
        ({ setHasDismissed80WarningBanner }) => {
            return {
                setHasDismissed80WarningBanner,
            };
        },
    );

    if (!shouldWarn80Used) {
        return null;
    }

    return (
        <ContactsOverageBannersLimitWarning
            onDismiss={() => {
                setHasDismissed80WarningBanner(true);
            }}
        >
            You've reached 80% of the contact limit
        </ContactsOverageBannersLimitWarning>
    );
};

const ContactsOverageBannersWarn90 = () => {
    const { shouldWarn90Used } = useOveragesShouldWarnBannerChecks();

    const { setHasDismissed90WarningBanner } = useAccountLocalStorage(
        ({ setHasDismissed90WarningBanner }) => {
            return {
                setHasDismissed90WarningBanner,
            };
        },
    );

    if (!shouldWarn90Used) {
        return null;
    }

    return (
        <ContactsOverageBannersLimitWarning
            onDismiss={() => {
                setHasDismissed90WarningBanner(true);
            }}
        >
            You've reached 90% of the contact limit
        </ContactsOverageBannersLimitWarning>
    );
};

const ContactsOverageBannersWarn100 = () => {
    const { shouldWarn100Used } = useOveragesShouldWarnBannerChecks();

    const { setHasDismissed100WarningBanner } = useAccountLocalStorage(
        ({ setHasDismissed100WarningBanner }) => {
            return {
                setHasDismissed100WarningBanner,
            };
        },
    );

    if (!shouldWarn100Used) {
        return null;
    }

    return (
        <ContactsOverageBannersLimitWarning
            onDismiss={() => {
                setHasDismissed100WarningBanner(true);
            }}
        >
            You've used over 100% of the contact limit
        </ContactsOverageBannersLimitWarning>
    );
};

const OverageBannerManagePlanButton = () => {
    const { isPermitted: canManagePlus } = usePermissions([AccountPermission.PLUS_MANAGEMENT]);
    const { validGivebutterPlusPlan, accountId } = useCurrentAccount();
    const overagePosthogProps = useOveragePosthogProperties();
    const { pushEvent } = usePosthog();

    if (!validGivebutterPlusPlan) {
        return null;
    }

    if (!canManagePlus) {
        return <p className="text-nowrap text-sm font-medium text-gray-700">Contact an admin</p>;
    }

    return (
        <Link
            to={{
                pathname: `/accounts/${accountId}/settings/about/subscription`,
                hash: `#${AnchorHashIds.BillingViewGivebutterPlusPlan}`,
            }}
        >
            <Button
                variant="none"
                size="sm"
                color="primary"
                className="font-medium"
                onClick={() => {
                    pushEvent(posthogEvents.DASHBOARD_OVERAGES_CONTACTS_BANNER_SELECTED, {
                        ...overagePosthogProps,
                    });
                }}
            >
                Manage plan
            </Button>
        </Link>
    );
};

const RecommendedPlan = ContactsOverageBannersRecommendedPlan;
const LimitWarning = ContactsOverageBannersLimitWarning;
const Warn80 = ContactsOverageBannersWarn80;
const Warn90 = ContactsOverageBannersWarn90;
const Warn100 = ContactsOverageBannersWarn100;

export {
    ContactsOverageBannersRecommendedPlan,
    ContactsOverageBannersLimitWarning,
    ContactsOverageBannersWarn80,
    ContactsOverageBannersWarn90,
    ContactsOverageBannersWarn100,
    //
    RecommendedPlan,
    LimitWarning,
    Warn80,
    Warn90,
    Warn100,
};
